// วันที่แบบไทย — ตัวแปลงชุดเดียวของทั้งระบบ
//
// input[type=date] ของเบราว์เซอร์แสดงปีเป็น ค.ศ. เสมอ และหน้าเว็บสั่งไม่ได้
// (รูปแบบมาจาก locale ของเครื่องผู้ใช้ ไม่ใช่จากโค้ด) ครูที่อ่าน 2026 แล้วคิดว่า
// เป็น พ.ศ. จะกรอกวันผิดไปทั้งใบโดยไม่มีอะไรเตือน — จึงมี ThaiDateInput
// (components/ThaiDateInput.js) ที่แสดง วว/ดด/ปปปป เป็น พ.ศ. ไว้ใช้แทน
//
// สิ่งที่ "ไม่" เปลี่ยนคือค่าที่วิ่งใน state และที่ส่ง server ยังเป็น 'yyyy-mm-dd'
// ค.ศ. เหมือน input[type=date] ทุกประการ — พ.ศ. อยู่แค่ชั้นที่คนอ่าน เพราะ
// คิวรีและคอลัมน์ DATE ทั้งระบบเป็น ค.ศ. ถ้าปล่อย พ.ศ. ลงไปถึง server เมื่อไหร่
// จะมีจุดใดจุดหนึ่งลืมแปลงกลับแล้วได้วันที่เพี้ยนไป 543 ปีแบบเงียบๆ

export const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

export const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
]

const pad2 = (n) => String(n).padStart(2, '0')

// ค่าที่รับได้มีสองแบบ และต้องอ่านคนละทาง:
//   'yyyy-mm-dd'  ค่าจาก input[type=date] / state ของหน้า — อ่านตัวเลขตรงๆ
//                 ห้ามโยนเข้า new Date() เพราะ JS ตีความเป็น UTC เที่ยงคืน
//   ISO datetime  ค่าจาก API — mysql2 คืน DATE เป็น Date object เที่ยงคืนตาม
//                 เวลาเครื่อง (UTC+7) พอผ่าน JSON กลายเป็น 17:00Z ของ "เมื่อวาน"
//                 ต้องอ่านเป็นเวลาท้องถิ่น ไม่ใช่ slice(0, 10) ซึ่งได้วันก่อนหน้า
export const dateParts = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return { day: value.getDate(), month: value.getMonth() + 1, year: value.getFullYear() }
  }
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim())
  if (plain) {
    const [, year, month, day] = plain.map(Number)
    return { day, month, year }
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1900) return null
  return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() }
}

// ค.ศ. (จาก DB หรือ state) -> วว/ดด/ปปปป พ.ศ. สำหรับกรอก/แสดงในฟอร์ม
export const toThaiDate = (value) => {
  const p = dateParts(value)
  return p ? `${pad2(p.day)}/${pad2(p.month)}/${p.year + 543}` : ''
}

// ค.ศ. -> '23 กันยายน 2569' สำหรับข้อความที่อ่านอย่างเดียว
export const toThaiDateLong = (value, { short = false } = {}) => {
  const p = dateParts(value)
  if (!p) return ''
  const months = short ? THAI_MONTHS_SHORT : THAI_MONTHS
  return `${p.day} ${months[p.month - 1]} ${p.year + 543}`
}

// ค่าอะไรก็ได้ -> 'yyyy-mm-dd' ตามเวลาท้องถิ่น (รูปแบบเดียวกับ input[type=date])
export const toISODate = (value) => {
  const p = dateParts(value)
  return p ? `${p.year}-${pad2(p.month)}-${pad2(p.day)}` : ''
}

// ค่าอะไรก็ได้ -> Date เที่ยงคืนตามเวลาท้องถิ่น (ให้ปฏิทินใช้)
export const toDateObject = (value) => {
  const p = dateParts(value)
  return p ? new Date(p.year, p.month - 1, p.day) : null
}

// ตรวจข้อความ วว/ดด/ปปปป (พ.ศ.) ว่าเป็นวันที่ที่มีอยู่จริง
// ช่วงปีกว้างไว้ 2400–2600 เพราะมีทั้งวันเกิดผู้ปกครองและวันหมดอายุเอกสารล่วงหน้า
export const isThaiDate = (text) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(text || '').trim())
  if (!m) return false
  const [, day, month, year] = m.map(Number)
  if (month < 1 || month > 12 || day < 1 || day > 31) return false
  if (year < 2400 || year > 2600) return false
  const ce = new Date(year - 543, month - 1, day)
  return ce.getDate() === day && ce.getMonth() === month - 1
}

// วว/ดด/ปปปป (พ.ศ.) -> 'yyyy-mm-dd' ค.ศ.  ข้อความที่ยังไม่ครบ/ไม่ถูกคืน ''
export const fromThaiDate = (text) => {
  if (!isThaiDate(text)) return ''
  const [day, month, year] = String(text).trim().split('/').map(Number)
  return `${year - 543}-${pad2(month)}-${pad2(day)}`
}

// เติม / ให้ระหว่างพิมพ์ จะได้กดแต่ตัวเลขรวดเดียว
// ถ้าคนพิมพ์ / เองหลังเลขหลักเดียว ให้เติม 0 นำหน้าให้ (9/7/2556 -> 09/07/2556)
export const thaiDateInput = (raw) => {
  const parts = String(raw).replace(/[^\d/]/g, '').split('/')
  const digits = parts
    .map((part, i) => (i < parts.length - 1 && part.length === 1 ? `0${part}` : part))
    .join('')
    .slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

// ชื่อวันแบบสั้นสำหรับหัวตารางปฏิทิน — date-fns locale th ให้มาเป็นคำเต็ม
// ('อาทิตย์') ซึ่งตัดสองตัวแรกแล้วได้ 'จั' 'เส' ที่ไม่มีใครเขียนกัน
export const THAI_WEEKDAYS_SHORT = {
  'อาทิตย์': 'อา',
  'จันทร์': 'จ',
  'อังคาร': 'อ',
  'พุธ': 'พ',
  'พฤหัสบดี': 'พฤ',
  'ศุกร์': 'ศ',
  'เสาร์': 'ส',
}
